import { useQuery } from '@tanstack/react-query'
import { FOLLOW_UP_SENT_STATUSES } from '@dealcloser/shared'
import type { ApiResponse } from '@/lib/api'
import { useApi } from '@/hooks/use-api'

// --- Types ---

export type TrendDirection = 'up' | 'down' | 'neutral'

export type Trend = {
  readonly value: number
  readonly direction: TrendDirection
}

export type DashboardStats = {
  readonly totalLeads: number
  readonly replyRate: number
  readonly meetingsBooked: number
  readonly contentEngagement: number
  readonly trends: {
    readonly totalLeads: Trend
    readonly replyRate: Trend
    readonly meetingsBooked: Trend
    readonly contentEngagement: Trend
  }
}

export type ActivityType = 'connection' | 'reply' | 'post' | 'enrichment'

export type ActivityItem = {
  readonly id: string
  readonly type: ActivityType
  readonly description: string
  readonly timestamp: string
  readonly entityUrl?: string
}

export type PipelineStage = {
  readonly name: string
  readonly count: number
  readonly percentage: number
}

// --- Backend response shapes ---

// Shape returned by GET /api/leads/stats (via leadsDb.getStats)
type LeadsStatsResponse = {
  readonly total?: number
  readonly by_status?: Partial<Record<string, number>>
  readonly avg_icp_score?: number
  readonly avg_intent_score?: number
  readonly avg_composite_score?: number
}

type PipelineResponse = readonly PipelineStage[]

// --- Placeholder data ---

const EMPTY_STATS: DashboardStats = {
  totalLeads: 0,
  replyRate: 0,
  meetingsBooked: 0,
  contentEngagement: 0,
  trends: {
    totalLeads: { value: 0, direction: 'neutral' },
    replyRate: { value: 0, direction: 'neutral' },
    meetingsBooked: { value: 0, direction: 'neutral' },
    contentEngagement: { value: 0, direction: 'neutral' },
  },
}

const NEUTRAL_TREND: Trend = { value: 0, direction: 'neutral' }

// A lead counts as contacted from its first DM onward, the same set
// computeStats() uses for a campaign's contacted count
// (packages/db/src/queries/campaigns.ts). The old denominator stopped at
// 'contacted', so every lead that moved on to a follow-up left it while the
// repliers stayed, and the card read near 100% on accounts with follow-ups.
const CONTACTED_STATUSES = [
  'contacted', 'dm_sent',
  ...FOLLOW_UP_SENT_STATUSES,
  'replied', 'qualified', 'meeting_booked', 'won',
]
const REPLIED_STATUSES = ['replied', 'qualified', 'meeting_booked', 'won'] as const

/** Percent of contacted leads who replied, to one decimal place. */
export function replyRateFromStatuses(byStatus: Partial<Record<string, number>>): number {
  const sum = (keys: readonly string[]) => keys.reduce((n, k) => n + (byStatus[k] ?? 0), 0)
  const contacted = sum(CONTACTED_STATUSES)
  return contacted > 0 ? Math.round((sum(REPLIED_STATUSES) / contacted) * 1000) / 10 : 0
}

// --- Fetcher helpers (accept apiFetch as parameter) ---

/**
 * Change from the previous period. Rates move in percentage points, counts
 * in percent (or the plain difference when there was nothing before).
 */
export function trendBetween(current: number, previous: number, kind: 'percent' | 'points' | 'count'): Trend {
  const delta =
    kind === 'percent'
      ? previous > 0 ? Math.round(((current - previous) / previous) * 1000) / 10 : current
      : Math.round((current - previous) * 10) / 10
  if (delta === 0) return NEUTRAL_TREND
  return { value: Math.abs(delta), direction: delta > 0 ? 'up' : 'down' }
}

async function fetchStats(
  apiFetch: <T>(path: string) => Promise<ApiResponse<T>>,
  query = '',
): Promise<DashboardStats> {
  const res = await apiFetch<LeadsStatsResponse>(`/api/leads/stats${query}`)
  if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load dashboard stats')
  const d = res.data
  const byStatus = d.by_status ?? {}
  const totalLeads = d.total ?? 0

  const replyRate = replyRateFromStatuses(byStatus)

  const meetingsBooked = byStatus['meeting_booked'] ?? 0

  return {
    totalLeads,
    replyRate,
    meetingsBooked,
    contentEngagement: 0, // populated separately by content worker stats
    trends: {
      totalLeads: NEUTRAL_TREND,
      replyRate: NEUTRAL_TREND,
      meetingsBooked: NEUTRAL_TREND,
      contentEngagement: NEUTRAL_TREND,
    },
  }
}

// Shape returned by GET /api/leads/activity
type ActivityLogEntry = {
  readonly id: string
  readonly action_type: string
  readonly entity_type: string | null
  readonly entity_id: string | null
  readonly details: string | null
  readonly created_at: string
}

async function fetchActivity(
  apiFetch: <T>(path: string) => Promise<ApiResponse<T>>,
  limit = 20,
): Promise<readonly ActivityItem[]> {
  const res = await apiFetch<{ readonly data: readonly ActivityLogEntry[]; readonly total: number }>(
    `/api/leads/activity?limit=${limit}`,
  )
  if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load activity feed')
  const entries = res.data.data ?? []
  return entries
    .map((entry) => mapActivityEntry(entry))
    .filter((item): item is ActivityItem => item !== null)
}

async function fetchPipeline(
  apiFetch: <T>(path: string) => Promise<ApiResponse<T>>,
  query = '',
): Promise<readonly PipelineStage[]> {
  const res = await apiFetch<PipelineResponse>(`/api/leads/pipeline${query}`)
  if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load pipeline data')
  return res.data
}

// Allowlist of action_types the dashboard surfaces. Defensive — backend
// already filters these — but if a new error type leaks through we'd rather
// drop it than render it as a generic "connection" event.
const TYPE_MAP: Record<string, ActivityType> = {
  connection_sent: 'connection',
  connection_accepted: 'connection',
  reply_received: 'reply',
  lead_qualified: 'reply',
  meeting_booked: 'reply',
  post_published: 'post',
  lead_enriched: 'enrichment',
  lead_created: 'enrichment',
  message_sent: 'reply',
  linkedin_connected: 'connection',
  linkedin_disconnected: 'connection',
}

// Map activity_log DB entry → ActivityItem for the dashboard feed.
// Returns null for unknown/system action types so they don't pollute the UI.
function mapActivityEntry(entry: ActivityLogEntry): ActivityItem | null {
  const type = TYPE_MAP[entry.action_type]
  if (!type) return null

  let description = entry.action_type.replace(/_/g, ' ')
  if (entry.details) {
    try {
      const details = JSON.parse(entry.details) as Record<string, unknown>
      if (typeof details.description === 'string') {
        description = details.description
      } else if (typeof details.lead_name === 'string') {
        description = `${entry.action_type.replace(/_/g, ' ')} — ${details.lead_name}`
      }
    } catch {
      // ignore JSON parse errors
    }
  }

  return {
    id: entry.id,
    type,
    description,
    timestamp: entry.created_at,
    entityUrl: entry.entity_id
      ? `/leads/${entry.entity_id}`
      : undefined,
  }
}

// --- Hooks ---

/**
 * `query` = the dashboard filters (useDashboardFilters().query); '' is every
 * lead. `null` skips the request (no previous period to compare with).
 */
export function useDashboardStats(query: string | null = '') {
  const apiFetch = useApi()

  return useQuery({
    queryKey: ['dashboard', 'stats', query],
    queryFn: () => fetchStats(apiFetch, query ?? ''),
    refetchInterval: 30_000,
    // Keep the last numbers on screen while a new filter loads.
    placeholderData: (prev) => prev ?? EMPTY_STATS,
    enabled: query !== null,
  })
}

export function useDashboardActivity(limit = 20) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: ['dashboard', 'activity', limit],
    queryFn: () => fetchActivity(apiFetch, limit),
    refetchInterval: 30_000,
  })
}

export function useDashboardPipeline(query = '') {
  const apiFetch = useApi()

  return useQuery({
    queryKey: ['dashboard', 'pipeline', query],
    queryFn: () => fetchPipeline(apiFetch, query),
    placeholderData: (prev) => prev,
    refetchInterval: 30_000,
  })
}

export function useIsEmptyDashboard(stats: DashboardStats | undefined): boolean {
  if (!stats) return true
  return (
    stats.totalLeads === 0 &&
    stats.meetingsBooked === 0 &&
    stats.contentEngagement === 0
  )
}
